import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { validateGitRepository, cloneGitRepository, scanDeployedApp, importDeployedApp, createBlankProject, fetchDeployedEnterprise } from '@/api/workbench'
import {
  registerGitSource, registerLocalPathSource, uploadZipSource, seedDockPrompt,
  type ReferenceSource,
} from '@/api/referenceSources'
import ReferenceSourceFields, {
  type SourceMode as ReferenceSourceMode,
} from '@/components/sources/ReferenceSourceFields'
import { listDeploymentProfiles } from '@/api/settings'
import { useProjectStore } from '@/store/projectStore'
import type { DeploymentProfile } from '@/types'
import toast from 'react-hot-toast'

type Status = 'idle' | 'validating' | 'valid' | 'invalid' | 'cloning' | 'success' | 'error'
type ImportTab = 'git' | 'deployed' | 'source'
type DeployStatus = 'idle' | 'scanning' | 'scanned' | 'importing' | 'success' | 'error'
type SourceStatus = 'idle' | 'working' | 'success' | 'error'
type SourceMode = ReferenceSourceMode

// Prefilled Ask-AI prompt for the freshly created replication project.
const ANALYZE_PROMPT =
  'Analyze the registered reference source code and produce the Adoption ' +
  'Blueprint for replicating this application, then show me the blueprint ' +
  'for review.'

interface ScannedObject {
  obj_name: string
  obj_type: string
  title: string
}

// What the import's source-control step actually did. The import itself can
// succeed while this fails (bad token, no permission to create a repository),
// so the outcome is reported rather than assumed.
function scmOutcome(scm?: {
  action?: string
  error?: string
  repository_url?: string
  version_tag?: string | null
  tag_message?: string
}): { ok: boolean; text: string } {
  if (!scm) return { ok: true, text: 'No repository was created — source control is off or unavailable.' }
  const detail = scm.error || ''
  switch (scm.action) {
    case 'created_and_pushed':
      // A first import creates the repository AND its baseline tag; a re-import
      // is an ordinary commit, so it reports no tag.
      if (scm.version_tag) {
        return { ok: true, text: `Repository created, pushed, and tagged ${scm.version_tag}.` }
      }
      if (scm.tag_message) {
        return {
          ok: false,
          text: `Repository created and pushed, but the baseline tag could not be created: ${scm.tag_message}`,
        }
      }
      return { ok: true, text: 'Repository created and pushed to source control.' }
    case 'pushed_to_existing':
      return { ok: true, text: 'Changes pushed to the existing repository.' }
    case 'created_push_failed':
      return { ok: false, text: `Repository created, but the push failed: ${detail || 'unknown error.'}` }
    case 'push_failed':
      return { ok: false, text: `Imported, but the push to source control failed: ${detail || 'unknown error.'}` }
    case 'scm_error':
      return { ok: false, text: `Imported, but source control failed: ${detail || 'unknown error.'}` }
    default:
      return { ok: false, text: `Imported, but source control returned an unexpected result${detail ? `: ${detail}` : '.'}` }
  }
}

export default function CreateFromGitPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { fetchProjects, selectProject } = useProjectStore()

  // Tab state (deep-linkable: ?tab=source)
  const [activeTab, setActiveTab] = useState<ImportTab>(() => {
    const t = searchParams.get('tab')
    return t === 'deployed' || t === 'source' ? t : 'git'
  })

  // --- Git import state ---
  const [repositoryName, setRepositoryName] = useState('')
  const [status, setStatus] = useState<Status>('idle')
  const [statusMessage, setStatusMessage] = useState('')
  const [clonedProjectId, setClonedProjectId] = useState<string | null>(null)

  // --- Deployed app import state ---
  const [profiles, setProfiles] = useState<DeploymentProfile[]>([])
  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null)
  const [appName, setAppName] = useState('')
  // Which enterprise's report overrides to import. The deployed reports trees
  // keep an enterprise's own JRXML/XSD in a folder named after it; blank means
  // the common folder, which is what every import did before.
  //
  // Not typed: it is read from the MY_ENTERPRISE table on the selected
  // profile's database, because one deployed install serves one enterprise and
  // that table is what says which. The field is therefore fixed — editable only
  // for the "ALL" install, which is not pinned to one. If the table cannot be
  // read the enterprise is unknown, and an import must not go ahead guessing
  // which report folder it is reading from (and would later deploy back into).
  const [enterprise, setEnterprise] = useState('')
  const [enterpriseEditable, setEnterpriseEditable] = useState(false)
  const [enterpriseLoading, setEnterpriseLoading] = useState(false)
  const [enterpriseError, setEnterpriseError] = useState('')
  const [deployStatus, setDeployStatus] = useState<DeployStatus>('idle')
  const [deployMessage, setDeployMessage] = useState('')
  const [scannedApp, setScannedApp] = useState<{ application: Record<string, string>; objects: ScannedObject[]; nav_count: number } | null>(null)
  const [selectedObjects, setSelectedObjects] = useState<Set<string>>(new Set())
  const [importedProjectId, setImportedProjectId] = useState<string | null>(null)
  const [importResult, setImportResult] = useState<{ objects_imported: ScannedObject[]; errors: { obj_name: string; error: string; severity?: string }[]; sql_files_count: number; jrxml_files_count: number } | null>(null)

  // --- Replicate-from-source-code state ---
  const [srcTitle, setSrcTitle] = useState('')
  const [srcProjectId, setSrcProjectId] = useState('')
  const [srcIdTouched, setSrcIdTouched] = useState(false)
  const [srcMode, setSrcMode] = useState<SourceMode>('git')
  const [srcGitUrl, setSrcGitUrl] = useState('')
  const [srcGitRef, setSrcGitRef] = useState('')
  const [srcLocalPath, setSrcLocalPath] = useState('')
  const [srcZipFile, setSrcZipFile] = useState<File | null>(null)
  const [srcStatus, setSrcStatus] = useState<SourceStatus>('idle')
  const [srcMessage, setSrcMessage] = useState('')
  const [srcUploadPct, setSrcUploadPct] = useState(0)
  const [srcRegistered, setSrcRegistered] = useState<ReferenceSource | null>(null)
  // Survives a failed registration so a retry doesn't recreate the project.
  const srcCreatedProjectRef = useRef<string | null>(null)

  // Load deployment profiles on mount
  useEffect(() => {
    listDeploymentProfiles().then(p => {
      setProfiles(p)
      const active = p.find(pr => pr.is_active)
      if (active) setSelectedProfileId(active.id)
    }).catch(() => {})
  }, [])

  // Read the Enterprise off the selected profile's database. Re-runs on every
  // profile change, including the active one picked on mount.
  useEffect(() => {
    if (!selectedProfileId) {
      setEnterprise(''); setEnterpriseEditable(false); setEnterpriseError('')
      return
    }
    let cancelled = false
    setEnterpriseLoading(true)
    setEnterpriseError('')
    fetchDeployedEnterprise(selectedProfileId)
      .then(res => {
        if (cancelled) return
        // "ALL" is not an enterprise, it is the absence of one: the field opens
        // up BLANK for the user to name a folder, which is how it behaved
        // before this was read from the database at all.
        setEnterpriseEditable(!!res.editable)
        setEnterprise(res.editable ? '' : (res.enterprise || ''))
      })
      .catch((err: unknown) => {
        if (cancelled) return
        const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
          || 'The Enterprise could not be read from this deployment profile.'
        setEnterprise('')
        setEnterpriseEditable(false)
        setEnterpriseError(msg)
      })
      .finally(() => { if (!cancelled) setEnterpriseLoading(false) })
    return () => { cancelled = true }
  }, [selectedProfileId])

  // --- Git import handlers ---
  const handleValidate = async () => {
    if (!repositoryName.trim()) {
      toast.error('Please enter a repository name')
      return
    }
    setStatus('validating')
    setStatusMessage('Checking repository accessibility...')
    try {
      const result = await validateGitRepository(repositoryName.trim())
      if (result.success) {
        setStatus('valid')
        setStatusMessage('Repository is valid and accessible.')
        toast.success('Repository validated')
      } else {
        setStatus('invalid')
        setStatusMessage(result.error || 'Repository is not accessible.')
        toast.error(result.error || 'Validation failed')
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to validate repository'
      setStatus('invalid')
      setStatusMessage(msg)
      toast.error(msg)
    }
  }

  const handleClone = async () => {
    if (!repositoryName.trim()) return
    setStatus('cloning')
    setStatusMessage('Cloning repository... This may take a moment.')
    try {
      const result = await cloneGitRepository(repositoryName.trim())
      if (result.success) {
        setStatus('success')
        setStatusMessage(
          result.ioflow_restore?.started
            ? 'Repository cloned successfully! Its IOFlow integration is being set up in the background. If its flows need a sign-in or a value, you will be asked when you open the app.'
            : 'Repository cloned successfully!',
        )
        setClonedProjectId(result.project_id || null)
        toast.success('Repository cloned successfully')
        await fetchProjects()
      } else {
        setStatus('error')
        setStatusMessage(result.error || 'Failed to clone repository.')
        toast.error(result.error || 'Clone failed')
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to clone repository'
      setStatus('error')
      setStatusMessage(msg)
      toast.error(msg)
    }
  }

  const handleGoToProject = async (projectId: string) => {
    await selectProject(projectId)
    navigate('/workbench')
  }

  // --- Deployed app handlers ---
  const handleScan = async () => {
    if (!selectedProfileId || !appName.trim()) {
      toast.error('Please select a profile and enter an Application ID')
      return
    }
    setDeployStatus('scanning')
    setDeployMessage('Connecting to database and scanning application...')
    setScannedApp(null)
    setSelectedObjects(new Set())
    setImportResult(null)
    setImportedProjectId(null)
    try {
      const result = await scanDeployedApp(selectedProfileId, appName.trim())
      if (result.success) {
        setDeployStatus('scanned')
        setDeployMessage(result.obj_count
          ? `Found ${result.obj_count} objects across ${result.nav_count} navigation items. Select objects to import.`
          : `No importable objects found across ${result.nav_count} navigation items. The application will be imported as a blank application.`)
        setScannedApp({ application: result.application, objects: result.objects || [], nav_count: result.nav_count })
        // Auto-select all objects
        setSelectedObjects(new Set((result.objects || []).map((o: ScannedObject) => o.obj_name)))
        toast.success(`Application scanned: ${result.obj_count} objects found`)
      } else {
        setDeployStatus('error')
        setDeployMessage(result.error || 'Scan failed')
        toast.error(result.error || 'Scan failed')
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to scan application'
      setDeployStatus('error')
      setDeployMessage(msg)
      toast.error(msg)
    }
  }

  const handleToggleObject = (objName: string) => {
    setSelectedObjects(prev => {
      const next = new Set(prev)
      if (next.has(objName)) next.delete(objName)
      else next.add(objName)
      return next
    })
  }

  const handleSelectAll = () => {
    if (!scannedApp) return
    setSelectedObjects(new Set(scannedApp.objects.map(o => o.obj_name)))
  }

  const handleDeselectAll = () => {
    setSelectedObjects(new Set())
  }

  const handleImport = async () => {
    if (!selectedProfileId || !appName.trim()) return
    if (enterpriseError) {
      // The button is disabled for this, but the guard keeps the rule with the
      // action rather than only with the control that triggers it.
      toast.error(enterpriseError)
      return
    }
    // An application with no importable objects imports BLANK (its settings
    // and menu tree only); one that has objects must import at least one.
    const isBlankImport = !!scannedApp && scannedApp.objects.length === 0
    if (scannedApp && !isBlankImport && selectedObjects.size === 0) {
      toast.error('Please select at least one object to import')
      return
    }
    setDeployStatus('importing')
    const objCount = selectedObjects.size
    const totalCount = scannedApp?.objects.length || 0
    const label = isBlankImport ? 'blank application'
      : objCount === totalCount ? 'all objects' : `${objCount} selected object(s)`
    setDeployMessage(`Importing ${label}... This may take a while for large applications.`)
    try {
      // Build selected objects list (null = all, [] = blank)
      let objectsToImport: { obj_name: string; obj_type: string }[] | null = null
      if (isBlankImport) {
        objectsToImport = []
      } else if (scannedApp && selectedObjects.size < scannedApp.objects.length) {
        objectsToImport = scannedApp.objects.filter(o => selectedObjects.has(o.obj_name))
          .map(o => ({ obj_name: o.obj_name, obj_type: o.obj_type }))
      }
      const result = await importDeployedApp(selectedProfileId, appName.trim(), objectsToImport,
                                             enterprise.trim())
      if (result.success) {
        setDeployStatus('success')
        const allIssues: { obj_name: string; error: string; severity?: string }[] = result.errors || []
        // Warnings ride the same list but describe an object that WAS imported
        // (e.g. a report whose JRXML design is missing) — they are not failures.
        const errCount = allIssues.filter(e => e.severity !== 'warning').length
        const warnCount = allIssues.length - errCount
        const importedCount = result.objects_imported?.length || 0
        setDeployMessage(
          (isBlankImport
            ? 'Import complete: blank application imported (no objects)'
            : `Import complete: ${importedCount} objects imported`) +
          (result.sql_files_count ? `, ${result.sql_files_count} SQL files extracted` : '') +
          (result.jrxml_files_count ? `, ${result.jrxml_files_count} report designs extracted` : '') +
          // A schema-mode visual is two files: the visual and the shared
          // dataset it reads from. Counted apart so an import that brought
          // the visuals but not their data sources says so.
          (result.schema_files_count ? `, ${result.schema_files_count} visual schemas extracted` : '') +
          // Read from the profile's InsightDB server, not the app server.
          (result.business_areas_count ? `, ${result.business_areas_count} business areas imported from InsightDB` : '') +
          (errCount ? `, ${errCount} errors` : '') +
          (warnCount ? `, ${warnCount} warnings` : '') +
          `. ${scmOutcome(result.scm).text}`
        )
        // The push runs AFTER the objects land, so it can fail on its own — a
        // rejected token leaves a perfectly good project with no repository.
        // Say so here; it used to be swallowed and the import read as clean.
        const scm = scmOutcome(result.scm)
        if (!scm.ok) toast(scm.text, { icon: '⚠️', duration: 8000 })
        setImportedProjectId(result.project_id || null)
        setImportResult({
          objects_imported: result.objects_imported || [],
          errors: allIssues,
          sql_files_count: result.sql_files_count || 0,
          jrxml_files_count: result.jrxml_files_count || 0,
        })
        toast.success('Application imported successfully')
        await fetchProjects()
      } else {
        setDeployStatus('error')
        setDeployMessage(result.error || 'Import failed')
        toast.error(result.error || 'Import failed')
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to import application'
      setDeployStatus('error')
      setDeployMessage(msg)
      toast.error(msg)
    }
  }

  // --- Replicate-from-source-code handlers ---
  const deriveProjectId = (title: string) =>
    title.trim().replace(/[^A-Za-z0-9_-]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40)

  const handleReplicate = async () => {
    const title = srcTitle.trim()
    const id = (srcProjectId || deriveProjectId(srcTitle)).trim()
    if (!title) { toast.error('Enter a name for the new application'); return }
    if (!id || !/^[A-Za-z0-9_-]+$/.test(id)) {
      toast.error('Project ID must be letters, digits, _ or -'); return
    }
    if (srcMode === 'git' && !srcGitUrl.trim()) { toast.error('Enter the git repository URL'); return }
    if (srcMode === 'path' && !srcLocalPath.trim()) { toast.error('Enter the absolute folder path'); return }
    if (srcMode === 'zip' && !srcZipFile) { toast.error('Choose a .zip of the source code'); return }

    setSrcStatus('working')
    setSrcUploadPct(0)
    try {
      // 1) Create the target project once (a retry after a failed
      // registration reuses it instead of erroring on "already exists").
      if (srcCreatedProjectRef.current !== id) {
        setSrcMessage('Creating the project...')
        await createBlankProject(id, title)
        srcCreatedProjectRef.current = id
        await fetchProjects()
      }
      // 2) Register the source (clone / validate / upload+extract + scan).
      setSrcMessage(
        srcMode === 'git' ? 'Cloning the repository (shallow)... This may take a while for large repositories.'
        : srcMode === 'path' ? 'Registering the folder and scanning the source tree...'
        : 'Uploading and extracting the archive...')
      let source: ReferenceSource
      if (srcMode === 'git') {
        source = await registerGitSource(id, srcGitUrl.trim(), srcGitRef.trim())
      } else if (srcMode === 'path') {
        source = await registerLocalPathSource(id, srcLocalPath.trim())
      } else {
        source = await uploadZipSource(id, srcZipFile as File, setSrcUploadPct)
      }
      setSrcRegistered(source)
      setSrcStatus('success')
      const langs = Object.keys(source.stats?.languages || {}).slice(0, 3).join(', ')
      const fws = (source.stats?.frameworks || []).join(', ')
      setSrcMessage(
        `Source registered: ${source.display} — ${source.stats?.files ?? 0} files`
        + (fws ? `, detected: ${fws}` : langs ? `, languages: ${langs}` : '')
        + '. Open the project and the Ask-AI agent will analyze it into an Adoption Blueprint for your review.')
      toast.success('Source code registered')
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        || (err as Error)?.message || 'Failed to register the source'
      setSrcStatus('error')
      setSrcMessage(msg)
      toast.error(msg)
    }
  }

  const handleOpenAndAnalyze = async () => {
    const id = srcCreatedProjectRef.current
    if (!id) return
    seedDockPrompt(id, ANALYZE_PROMPT)
    await selectProject(id)
    navigate('/workbench')
  }

  const getStatusIcon = (s: Status | DeployStatus): { icon: string; color: string } => {
    switch (s) {
      case 'validating': case 'scanning': case 'cloning': case 'importing':
        return { icon: 'hourglass_top', color: 'var(--color-warning)' }
      case 'valid': case 'scanned': case 'success':
        return { icon: 'check_circle', color: 'var(--color-accent)' }
      case 'invalid': case 'error':
        return { icon: 'error', color: 'var(--color-error)' }
      default:
        return { icon: 'info', color: 'var(--color-text-secondary)' }
    }
  }

  const isLoading = (s: Status | DeployStatus) =>
    s === 'validating' || s === 'cloning' || s === 'scanning' || s === 'importing'

  const OBJ_TYPE_LABELS: Record<string, string> = {
    T: 'Transaction', V: 'Visual', D: 'Dashboard', R: 'Report', G: 'General Process', W: 'Workflow', S: 'Smart Page', M: 'Visual Schema',
  }

  return (
    <div style={{ maxWidth: 640, margin: '0 auto', animation: 'fadeIn 0.25s ease-out' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => navigate('/workbench')}
          style={{ marginBottom: '1rem' }}
        >
          <span className="material-icons" style={{ fontSize: 19 }}>arrow_back</span>
          Back to Workbench
        </button>
        <h1 style={{ fontSize: '1.5625rem', fontWeight: 600, marginBottom: '0.375rem' }}>
          Import Project
        </h1>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: '1rem' }}>
          Import a project from a Git repository or a deployed application, or replicate an
          existing app from its source code.
        </p>
      </div>

      {/* Tab Switcher */}
      <div style={{
        display: 'flex',
        gap: 0,
        marginBottom: '1.5rem',
        background: 'var(--color-bg-secondary)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--color-border)',
        padding: 4,
      }}>
        {([
          { key: 'git' as ImportTab, label: 'Import from Git', icon: 'code' },
          { key: 'deployed' as ImportTab, label: 'Import from Deployed App', icon: 'cloud_download' },
          { key: 'source' as ImportTab, label: 'Replicate from Source Code', icon: 'auto_awesome' },
        ]).map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              padding: '0.625rem 1rem',
              borderRadius: 'calc(var(--radius-lg) - 2px)',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.9375rem',
              fontWeight: 500,
              transition: 'all 0.15s ease',
              background: activeTab === tab.key ? 'var(--color-accent)' : 'transparent',
              color: activeTab === tab.key ? '#fff' : 'var(--color-text-secondary)',
            }}
          >
            <span className="material-icons" style={{ fontSize: 19 }}>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Git Import Tab */}
      {activeTab === 'git' && (
        <div style={{
          background: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          padding: '1.75rem',
        }}>
          <div style={{ marginBottom: '1.25rem' }}>
            <label htmlFor="repo-name">Repository Name</label>
            <input
              id="repo-name"
              type="text"
              value={repositoryName}
              onChange={(e) => {
                setRepositoryName(e.target.value)
                if (status !== 'idle') { setStatus('idle'); setStatusMessage('') }
              }}
              placeholder="owner/repository-name"
              disabled={status === 'cloning'}
            />
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4, display: 'block' }}>
              Enter the repository in the format: username/repository-name (e.g., johndoe/my-app)
            </span>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
            <button
              className="btn btn-secondary"
              onClick={handleValidate}
              disabled={!repositoryName.trim() || status === 'validating' || status === 'cloning'}
            >
              {status === 'validating' ? (
                <><span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> Validating...</>
              ) : (
                <><span className="material-icons" style={{ fontSize: 19 }}>verified</span> Validate</>
              )}
            </button>
            <button
              className="btn btn-primary"
              onClick={handleClone}
              disabled={status !== 'valid'}
            >
              {status === 'cloning' ? (
                <><span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> Cloning...</>
              ) : (
                <><span className="material-icons" style={{ fontSize: 19 }}>cloud_download</span> Clone Repository</>
              )}
            </button>
          </div>

          {statusMessage && (
            <StatusBox status={status} message={statusMessage} getIcon={getStatusIcon} isLoading={isLoading} />
          )}

          {status === 'success' && clonedProjectId && (
            <div style={{ marginTop: '1rem' }}>
              <button className="btn btn-primary btn-lg w-full" onClick={() => handleGoToProject(clonedProjectId)}>
                <span className="material-icons" style={{ fontSize: 19 }}>open_in_new</span>
                Open Project in Workbench
              </button>
            </div>
          )}
        </div>
      )}

      {/* Deployed App Import Tab */}
      {activeTab === 'deployed' && (
        <div style={{
          background: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          padding: '1.75rem',
        }}>
          {profiles.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
              <span className="material-icons" style={{ fontSize: 41, color: 'var(--color-text-secondary)', marginBottom: 12, display: 'block' }}>
                settings_suggest
              </span>
              <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1rem', fontSize: '1rem' }}>
                No deployment profiles configured. Create a deployment profile in Settings first.
              </p>
              <button className="btn btn-secondary" onClick={() => navigate('/settings')}>
                <span className="material-icons" style={{ fontSize: 19 }}>settings</span>
                Go to Settings
              </button>
            </div>
          ) : (
            <>
              {/* Profile Selection */}
              <div style={{ marginBottom: '1.25rem' }}>
                <label htmlFor="deploy-profile">Deployment Profile</label>
                <select
                  id="deploy-profile"
                  value={selectedProfileId || ''}
                  onChange={(e) => {
                    setSelectedProfileId(e.target.value ? Number(e.target.value) : null)
                    if (deployStatus !== 'idle') {
                      setDeployStatus('idle'); setDeployMessage(''); setScannedApp(null)
                      setSelectedObjects(new Set()); setImportResult(null); setImportedProjectId(null)
                    }
                  }}
                  disabled={deployStatus === 'importing'}
                  style={{
                    width: '100%',
                    padding: '0.625rem 0.75rem',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-bg)',
                    color: 'var(--color-text)',
                    fontSize: '0.9375rem',
                  }}
                >
                  <option value="">Select a deployment profile...</option>
                  {profiles.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.profile_name} ({p.application_database}{p.is_active ? ' - Active' : ''})
                    </option>
                  ))}
                </select>
              </div>

              {/* App Name Input */}
              <div style={{ marginBottom: '1.25rem' }}>
                <label htmlFor="app-name">Application ID</label>
                <input
                  id="app-name"
                  type="text"
                  value={appName}
                  onChange={(e) => {
                    setAppName(e.target.value)
                    if (deployStatus !== 'idle') {
                      setDeployStatus('idle'); setDeployMessage(''); setScannedApp(null)
                      setSelectedObjects(new Set()); setImportResult(null); setImportedProjectId(null)
                    }
                  }}
                  placeholder="e.g., PMS, SCM, HRM"
                  disabled={deployStatus === 'importing'}
                />
                <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4, display: 'block' }}>
                  The APP_NAME as stored in APPL_MST / ITM2MENU tables.
                </span>
              </div>

              {/* Enterprise Input — filled from MY_ENTERPRISE, not typed */}
              <div style={{ marginBottom: '1.25rem' }}>
                <label htmlFor="enterprise">Enterprise</label>
                <input
                  id="enterprise"
                  type="text"
                  value={enterprise}
                  onChange={(e) => setEnterprise(e.target.value)}
                  readOnly={!enterpriseEditable}
                  placeholder={
                    enterpriseLoading ? 'Reading MY_ENTERPRISE…'
                      : enterpriseEditable ? 'Optional — e.g., ACME'
                      : ''
                  }
                  disabled={deployStatus === 'importing' || enterpriseLoading}
                  style={!enterpriseEditable
                    ? { background: 'var(--color-bg-secondary)', cursor: 'not-allowed' }
                    : undefined}
                />
                {enterpriseError ? (
                  <span style={{
                    fontSize: '0.8125rem', color: 'var(--color-error)', marginTop: 4,
                    display: 'block',
                  }}>
                    {enterpriseError}
                  </span>
                ) : (
                  <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4, display: 'block' }}>
                    {enterpriseEditable
                      ? 'This database is installed for ALL enterprises, so the Enterprise is yours to name. ' +
                        'Reports it overrides are imported from its own folder in the app server\'s JRXML and ' +
                        'XSD trees; the rest come from the common folders. Leave blank to import only the common ones.'
                      : 'Read from the MY_ENTERPRISE table on the selected deployment profile\'s database. ' +
                        'Reports this enterprise overrides are imported from its own folder in the app ' +
                        'server\'s JRXML and XSD trees; the rest come from the common folders.'}
                  </span>
                )}
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
                <button
                  className="btn btn-secondary"
                  onClick={handleScan}
                  disabled={!selectedProfileId || !appName.trim() || deployStatus === 'scanning' || deployStatus === 'importing'}
                >
                  {deployStatus === 'scanning' ? (
                    <><span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> Scanning...</>
                  ) : (
                    <><span className="material-icons" style={{ fontSize: 19 }}>search</span> Scan</>
                  )}
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleImport}
                  disabled={deployStatus !== 'scanned'
                            || (!!scannedApp && scannedApp.objects.length > 0 && selectedObjects.size === 0)
                            || enterpriseLoading || !!enterpriseError}
                >
                  {deployStatus === 'importing' ? (
                    <><span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> Importing...</>
                  ) : (
                    <><span className="material-icons" style={{ fontSize: 19 }}>download</span>
                      {scannedApp && scannedApp.objects.length === 0
                        ? 'Import as Blank Application'
                        : `Import${scannedApp && selectedObjects.size < scannedApp.objects.length
                            ? ` (${selectedObjects.size})`
                            : ' All'}`}
                    </>
                  )}
                </button>
              </div>

              {/* Status Messages */}
              {deployMessage && (
                <StatusBox status={deployStatus} message={deployMessage} getIcon={getStatusIcon} isLoading={isLoading} />
              )}

              {/* Scanned Objects Preview with Selection */}
              {scannedApp && (
                <div style={{
                  marginTop: '1rem',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)',
                  overflow: 'hidden',
                }}>
                  {/* App Info + Select Controls */}
                  <div style={{
                    padding: '0.75rem 1rem',
                    background: 'var(--color-bg)',
                    borderBottom: '1px solid var(--color-border)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}>
                    <span className="material-icons" style={{ fontSize: 19, color: 'var(--color-accent)' }}>apps</span>
                    <span style={{ fontWeight: 600, fontSize: '0.9375rem' }}>
                      {scannedApp.application.title || scannedApp.application.id}
                    </span>
                    {scannedApp.application.description && (
                      <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', marginLeft: 4 }}>
                        — {scannedApp.application.description}
                      </span>
                    )}
                  </div>
                  {/* Select All / Deselect All Controls */}
                  {scannedApp.objects.length > 0 && (
                    <div style={{
                      padding: '0.5rem 1rem',
                      background: 'var(--color-bg)',
                      borderBottom: '1px solid var(--color-border)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontSize: '0.875rem',
                    }}>
                      <span style={{ color: 'var(--color-text-secondary)' }}>
                        {selectedObjects.size} of {scannedApp.objects.length} objects selected
                      </span>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={handleSelectAll}
                          disabled={selectedObjects.size === scannedApp.objects.length}
                          style={{ fontSize: '0.8125rem', padding: '2px 8px' }}
                        >
                          Select All
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={handleDeselectAll}
                          disabled={selectedObjects.size === 0}
                          style={{ fontSize: '0.8125rem', padding: '2px 8px' }}
                        >
                          Deselect All
                        </button>
                      </div>
                    </div>
                  )}
                  {/* Objects List with Checkboxes */}
                  <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                    {scannedApp.objects.length === 0 ? (
                      <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                        No importable objects found — the application will be imported as a blank application.
                      </div>
                    ) : (
                      scannedApp.objects.map((obj, i) => {
                        const isSelected = selectedObjects.has(obj.obj_name)
                        return (
                          <div
                            key={obj.obj_name}
                            onClick={() => handleToggleObject(obj.obj_name)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              padding: '0.5rem 1rem',
                              borderBottom: i < scannedApp.objects.length - 1 ? '1px solid var(--color-border)' : 'none',
                              fontSize: '0.875rem',
                              cursor: 'pointer',
                              background: isSelected ? 'var(--color-bg)' : 'transparent',
                              opacity: isSelected ? 1 : 0.55,
                              transition: 'all 0.1s ease',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleObject(obj.obj_name)}
                              onClick={e => e.stopPropagation()}
                              style={{ width: 16, height: 16, flexShrink: 0, cursor: 'pointer', accentColor: 'var(--color-accent)' }}
                            />
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 22,
                              height: 22,
                              borderRadius: 4,
                              background: 'var(--color-accent)',
                              color: '#fff',
                              fontSize: '0.8125rem',
                              fontWeight: 700,
                              flexShrink: 0,
                            }}>
                              {obj.obj_type}
                            </span>
                            <span style={{ fontWeight: 500 }}>{obj.obj_name}</span>
                            <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.8125rem' }}>
                              {OBJ_TYPE_LABELS[obj.obj_type] || obj.obj_type}
                            </span>
                            {obj.title && (
                              <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.8125rem', marginLeft: 'auto' }}>
                                {obj.title}
                              </span>
                            )}
                          </div>
                        )
                      })
                    )}
                  </div>
                </div>
              )}

              {/* Import Results */}
              {importResult && importResult.errors.length > 0 && (
                <div style={{
                  marginTop: '0.75rem',
                  padding: '0.75rem 1rem',
                  background: 'var(--color-bg)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)',
                }}>
                  {([
                    { severity: 'error', icon: 'error', color: 'var(--color-danger)',
                      items: importResult.errors.filter(e => e.severity !== 'warning'),
                      label: (n: number) => `${n} object(s) could not be imported:` },
                    { severity: 'warning', icon: 'warning', color: 'var(--color-warning)',
                      items: importResult.errors.filter(e => e.severity === 'warning'),
                      label: (n: number) => `${n} object(s) imported with warnings:` },
                  ] as const).filter(g => g.items.length > 0).map(group => (
                    <div key={group.severity} style={{ marginBottom: '0.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: '0.5rem' }}>
                        <span className="material-icons" style={{ fontSize: 17, color: group.color }}>{group.icon}</span>
                        <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                          {group.label(group.items.length)}
                        </span>
                      </div>
                      {group.items.map((e, i) => (
                        <div key={`${e.obj_name}-${i}`} style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginLeft: 22 }}>
                          <strong>{e.obj_name}</strong>: {e.error}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {/* Success - Go to project */}
              {deployStatus === 'success' && importedProjectId && (
                <div style={{ marginTop: '1rem' }}>
                  <button className="btn btn-primary btn-lg w-full" onClick={() => handleGoToProject(importedProjectId)}>
                    <span className="material-icons" style={{ fontSize: 19 }}>open_in_new</span>
                    Open Project in Workbench
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Replicate from Source Code Tab */}
      {activeTab === 'source' && (
        <div style={{
          background: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          padding: '1.75rem',
        }}>
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9375rem', marginBottom: '1.25rem' }}>
            Point at an existing application's source code — any language or framework —
            and the Ask-AI agent will study it (read-only) and replicate its functionality
            as a Twasta application, in phases you review.
          </p>

          {/* New application name */}
          <div style={{ marginBottom: '1rem' }}>
            <label htmlFor="src-title">New Application Name</label>
            <input
              id="src-title"
              type="text"
              value={srcTitle}
              onChange={(e) => {
                setSrcTitle(e.target.value)
                if (!srcIdTouched) setSrcProjectId(deriveProjectId(e.target.value))
                if (srcStatus !== 'idle' && srcStatus !== 'working') { setSrcStatus('idle'); setSrcMessage('') }
              }}
              placeholder="e.g., Acme ERP"
              disabled={srcStatus === 'working' || srcStatus === 'success'}
            />
          </div>
          <div style={{ marginBottom: '1.25rem' }}>
            <label htmlFor="src-id">Project ID</label>
            <input
              id="src-id"
              type="text"
              value={srcProjectId}
              onChange={(e) => { setSrcIdTouched(true); setSrcProjectId(e.target.value) }}
              placeholder="acme_erp"
              disabled={srcStatus === 'working' || srcStatus === 'success'}
            />
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4, display: 'block' }}>
              Letters, digits, _ or - only.
            </span>
          </div>

          {/* Source mode selector */}
          {/* Shared with the Ask-AI composer's "Add a source" menu, so the two
              entry points can never drift. */}
          <ReferenceSourceFields
            value={{
              mode: srcMode,
              gitUrl: srcGitUrl,
              gitRef: srcGitRef,
              localPath: srcLocalPath,
              zipFile: srcZipFile,
            }}
            onChange={(v) => {
              setSrcMode(v.mode)
              setSrcGitUrl(v.gitUrl)
              setSrcGitRef(v.gitRef)
              setSrcLocalPath(v.localPath)
              setSrcZipFile(v.zipFile)
            }}
            disabled={srcStatus === 'working' || srcStatus === 'success'}
            uploadPct={srcStatus === 'working' && srcMode === 'zip' ? srcUploadPct : 0}
          />

          {/* Action */}
          {srcStatus !== 'success' && (
            <button
              className="btn btn-primary"
              onClick={handleReplicate}
              disabled={srcStatus === 'working'}
            >
              {srcStatus === 'working' ? (
                <><span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> Working...</>
              ) : (
                <><span className="material-icons" style={{ fontSize: 19 }}>auto_awesome</span> Create Project &amp; Register Source</>
              )}
            </button>
          )}

          {/* Status */}
          {srcMessage && (
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.625rem',
              padding: '0.875rem',
              marginTop: '1rem',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-bg)',
              border: '1px solid var(--color-border)',
            }}>
              {srcStatus === 'working' ? (
                <span className="spinner" style={{ width: 18, height: 18, borderWidth: 2, flexShrink: 0, marginTop: 1 }} />
              ) : (
                <span className="material-icons" style={{
                  fontSize: 21, flexShrink: 0, marginTop: 1,
                  color: srcStatus === 'success' ? 'var(--color-accent)'
                    : srcStatus === 'error' ? 'var(--color-error)'
                    : 'var(--color-text-secondary)',
                }}>
                  {srcStatus === 'success' ? 'check_circle' : srcStatus === 'error' ? 'error' : 'info'}
                </span>
              )}
              <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>{srcMessage}</div>
            </div>
          )}

          {srcStatus === 'success' && srcRegistered && (
            <div style={{ marginTop: '1rem' }}>
              <button className="btn btn-primary btn-lg w-full" onClick={handleOpenAndAnalyze}>
                <span className="material-icons" style={{ fontSize: 19 }}>open_in_new</span>
                Open Project &amp; Analyze the Source
              </button>
            </div>
          )}
        </div>
      )}

      {/* Help Section */}
      <div style={{
        marginTop: '1.5rem',
        padding: '1.25rem',
        background: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.75rem' }}>
          <span className="material-icons" style={{ fontSize: 19, color: 'var(--color-info)' }}>help_outline</span>
          <h3 style={{ fontSize: '1rem', fontWeight: 600 }}>Prerequisites</h3>
        </div>
        <ul style={{ listStyle: 'disc', paddingLeft: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          {activeTab === 'source' ? (
            <>
              <li style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                The source application can be in ANY language or framework — Laravel, Django,
                Rails, Spring, Node, .NET, plain SQL, and more.
              </li>
              <li style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                The source tree is read-only reference material — it is never modified, and it
                is not imported into your project or its git repository.
              </li>
              <li style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                The Ask-AI agent first produces an Adoption Blueprint (modules, screens, rules,
                and a phase plan) for your review — nothing is built until you confirm the scope.
              </li>
              <li style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                Large applications are adopted in phases; you review each phase before the next
                one is built, and the work is resumable at any time.
              </li>
            </>
          ) : activeTab === 'git' ? (
            <>
              <li style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                Make sure you have configured your SCM credentials in{' '}
                <span onClick={() => navigate('/settings')} style={{ color: 'var(--color-accent)', cursor: 'pointer' }}>Settings</span>.
              </li>
              <li style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                The repository must be accessible with your configured credentials.
              </li>
              <li style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                Private repositories require proper authentication tokens.
              </li>
            </>
          ) : (
            <>
              <li style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                Create a deployment profile in{' '}
                <span onClick={() => navigate('/settings')} style={{ color: 'var(--color-accent)', cursor: 'pointer' }}>Settings</span>{' '}
                with the target database connection details.
              </li>
              <li style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                The metadata_path in the profile should point to the deployed XML metadata directory.
              </li>
              <li style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                The Application ID is the APP_NAME as stored in the APPL_MST table.
              </li>
              <li style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                The system will extract navigation, transaction models, validations, lookups, and business logic functions.
              </li>
            </>
          )}
        </ul>
      </div>
    </div>
  )
}

function StatusBox({
  status,
  message,
  getIcon,
  isLoading,
}: {
  status: Status | DeployStatus
  message: string
  getIcon: (s: Status | DeployStatus) => { icon: string; color: string }
  isLoading: (s: Status | DeployStatus) => boolean
}) {
  const statusLabels: Record<string, string> = {
    validating: 'Validating...',
    valid: 'Repository Valid',
    invalid: 'Validation Failed',
    cloning: 'Cloning Repository...',
    success: 'Complete',
    error: 'Failed',
    scanning: 'Scanning Application...',
    scanned: 'Scan Complete',
    importing: 'Importing Application...',
  }

  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: '0.625rem',
      padding: '0.875rem',
      borderRadius: 'var(--radius-md)',
      background: 'var(--color-bg)',
      border: '1px solid var(--color-border)',
    }}>
      {isLoading(status) ? (
        <span className="spinner" style={{ width: 18, height: 18, borderWidth: 2, flexShrink: 0, marginTop: 1 }} />
      ) : (
        <span className="material-icons" style={{ fontSize: 21, color: getIcon(status).color, flexShrink: 0, marginTop: 1 }}>
          {getIcon(status).icon}
        </span>
      )}
      <div>
        <div style={{ fontSize: '0.9375rem', color: 'var(--color-text)', fontWeight: 500 }}>
          {statusLabels[status] || ''}
        </div>
        <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginTop: 2 }}>
          {message}
        </div>
      </div>
    </div>
  )
}
